/* eslint-disable import/no-anonymous-default-export */

import { convertDateStringsToTimestamps } from "../../../../db/schema/schemaHooks";
import { createId, isValidCuid2 } from "../../../../utils/cuid2Utils";

/**
 * database configuration
 * ----------------------------------------
 * Format matches feature config modules so it is auto-collected by your existing
 * Webpack loader (require.context) alongside other feature configs.
 *
 * Version semantics (store.object.data): "MAJOR.MINOR.PATCH"
 * - MAJOR (store): Dexie store definition change → requires DB version bump.
 * - MINOR (object): object model changes → coerce existing records.
 * - PATCH (data): data-only adjustments (upserts) → no schema change.
 */

const VERSION = "1.1.1";

/**
 * Default store object model for settings.
 *
 *  * Update level must be MINOR for model changes to take effect
 *
 * Adding keys to data objects will occur when the key is added to this model
 * Removing keys from data objects requires using the keyPruning function in the transform hook
 *
 * @constant {Object} modelStore - represents the store and may be the parent of all data objects.
 *
 * @property {string} id - Unique identifier for the store. Must be set to the store's name.
 * @property {string} label -Human-readable label for the store which may be used in UI
 * @property {string} type - Type identifier for the store
 * @property {string} description -Longer description or help text explaining the purpose of the store.
 * @property {Array.<{id:number,label:string,order:number}>} categories
 *   Array of category objects available to this store. Each category has:
 *     - id {number}: numeric category identifier
 *     - label {string}: human-readable category label
 *     - order {number}: numeric ordering index for categories
 *
 *   Categories are used to group settings and Labels are used in UI
 *
 * @property {string|null} image - URL or path to an image associated with the setting, or null.
 * @property {string} version - Semantic/version string representing the version of this store definition.
 * @property {number} created - Creation timestamp in milliseconds since the epoch (Date.now()).
 * @property {number} modified - Last-modified timestamp in milliseconds since the epoch (Date.now()).
 */
export const modelStore = {
  id: "", // must be set to stores name
  label: "",
  type: "",
  description: "",
  categories: [{ id: 0, label: "", order: 0 }],
  image: "",
  version: "", // version of this store
  created: Date.now(),
  modified: Date.now(),
};

/**
 * Default model for data entry.
 *
 * Update level must be MINOR for model changes to take effect
 *
 * Adding keys to data objects will occur when the key is added to this model
 * Removing keys from data objects requires using the keyPruning function in the transform hook
 *
 * @constant {Object} modelData
 *
 * @property {string} id - CUID2 Unique identifier for the setting
 * @property {string} title - Title of the tale
 * @property {number} startDate - Start timestamp of the tale
 * @property {number} endDate - End timestamp of the tale
 * @property {string} description - Description of the tale
 * @property {string} timeZone - Primary TimeZone abbreviation of the tale
 * @property {Array.<{imageId: string}>} images a list of images for this track.
 * @property {number} created - Creation timestamp in milliseconds since epoch (Date.now()).
 * @property {number} modified - Last-modified timestamp in milliseconds since epoch (Date.now()).
 */
export const modelData = {
  coordinates: {
    accuracy: 0,
    altitude: 0,
    heading: 0,
    latitude: 0,
    longitude: 0,
    speed: 0,
  },
  title: "",
  description: "",
  tags: [],
  taleId: "",
  id: "",
  type: "",
  subType: "",
  timeZone: "",
  images: [],
  created: Date.now(),
  updated: Date.now(),
};

// #region export Default
/**
 * Migration Configuration Object
 *
 * Defines a single migration step for a store, including version information,
 * schema changes, data updates, and transformation hooks. Each configuration
 * file represents one version increment (MAJOR, MINOR, or PATCH).
 *
 * @typedef {Object} MigrationConfig
 *
 * ============================================================================
 * VERSION MANAGEMENT
 * ============================================================================
 *
 * @property {string} version
 *   REQUIRED. Semantic version string (MAJOR.MINOR.PATCH).
 *   Must match the version in the config file name.
 *
 *   Versioning Rules:
 *   - New stores start at 1.1.1
 *   - PATCH (position 3): Data object key values (1.1.1 -> 1.1.2)
 *   - MINOR (position 2): Model updates (1.1.x -> 1.2.1)
 *   - MAJOR (position 1): Store schema updates (1.x.x -> 2.1.1)
 *
 *   Constraints:
 *   - Cannot skip versions (1.1.1 cannot jump to 1.1.5)
 *   - After incrementing MINOR, PATCH resets to 1
 *   - After incrementing MAJOR, both MINOR and PATCH reset to 1
 *
 *   Examples:
 *   - 1.1.1 -> 1.1.2 ✓ (patch increment)
 *   - 1.1.2 -> 1.2.1 ✓ (minor increment, patch resets)
 *   - 1.2.7 -> 2.1.1 ✓ (major increment, both reset)
 *   - 1.1.1 -> 1.1.5 ✗ (skips versions 1.1.2-1.1.4)
 *   - 1.1.2 -> 1.2.2 ✗ (patch should reset to 1)
 *
 * @property {boolean} forceUpdate
 *   Controls whether the user can skip this migration.
 *   - true: Skip user prompt, apply update automatically
 *   - false: Allow user to decline the update
 *
 *   Future Behavior:
 *   When true, will prompt user to backup before forcing the update.
 *
 * @property {string} type
 *   Human-readable update type indicator: "MAJOR" | "MINOR" | "PATCH"
 *   For convenience and quick reference only.
 *   Not used in code to determine update behavior.
 *
 * ============================================================================
 * DOCUMENTATION
 * ============================================================================
 *
 * @property {string[]} changeLog
 *   Array of specific technical changes included in this config.
 *   Intended for developer documentation and internal tracking.
 *
 *   Examples:
 *   - "Removing description index"
 *   - "Adding email field to user model"
 *   - "Migrating data from oldField to newField"
 *
 * @property {string} changeDescription
 *   User-facing description of the update.
 *   Displayed to users when prompted to apply the migration.
 *   Should be clear and non-technical.
 *
 *   Example:
 *   "This update improves search performance by optimizing the index structure."
 *
 * ============================================================================
 * STORE SCHEMA (MAJOR UPDATES)
 * ============================================================================
 *
 * @property {Object} stores
 *   Defines the IndexedDB store schema and indexes.
 *
 *   Rules:
 *   - Only ONE store definition per config file
 *   - Additional stores will be ignored
 *   - Store modifications require a MAJOR version bump
 *   - Modifying indexes (add/remove) requires a new MAJOR update config
 *
 *   Format:
 *   The key is the store name, the value is a comma-separated string of indexes.
 *   First index is the keyPath, remaining are indexed fields.
 *
 *   @example
 *   stores: {
 *     settings: "id,modified,category"
 *     // "id" = keyPath
 *     // "modified" and "category" = indexed fields for queries
 *   }
 *
 *   Index Modifications:
 *   - Add index: Include new index in string, create MAJOR config
 *   - Remove index: Exclude index from string, create MAJOR config
 *
 * ============================================================================
 * MODEL UPDATES (MINOR UPDATES)
 * ============================================================================
 *
 * @property {Object} model
 *   Defines the data models for store objects and data objects.
 *
 * @property {Object} model.storeObject
 *   Model for the single store metadata object.
 *   This is the special object with id "__history" that tracks store state.
 *
 *   Model Modifications:
 *   - Add keys: Include new key in model, create MINOR config
 *   - Remove keys: Remove key from model, create MINOR config
 *
 * @property {Object} model.dataObject
 *   Model for all data objects in the store (excluding storeObject).
 *   All data rows will conform to this model.
 *
 *   Model Modifications:
 *   - Add keys: Include new key in model, create MINOR config
 *   - Remove keys: Remove key from model AND configure transform hook
 *     with keyPruning to remove the keys from existing data
 *     (keys are not removed automatically)
 *
 *   @example
 *   model: {
 *     storeObject: {
 *       id: "__history",
 *       version: "1.2.1",
 *       created: Date.now(),
 *       modified: Date.now()
 *     },
 *     dataObject: {
 *       id: "",
 *       name: "",
 *       email: "", // newly added field
 *       created: Date.now(),
 *       modified: Date.now()
 *     }
 *   }
 *
 * ============================================================================
 * DATA POPULATION (PATCH UPDATES)
 * ============================================================================
 *
 * @property {Array} data
 *   Array of data objects to add to the store during this update.
 *   Empty array if no data needs to be added.
 *
 *   Initial Store Setup (v1.1.1):
 *   - Optionally seed the storeObject to lock starting versions
 *   - If not seeded, fallback creates object from model with version
 *     and timestamps
 *
 *   Each object in the array should conform to the dataObject model.
 *
 *   @example
 *   data: [
 *     {
 *       id: "default-1",
 *       name: "Default Setting",
 *       value: true,
 *       created: 1699564800000,
 *       modified: 1699564800000
 *     }
 *   ]
 *
 * ============================================================================
 * TRANSFORMATION HOOKS
 * ============================================================================
 *
 * @property {Object} hooks
 *   Optional transformation and lifecycle hooks.
 *   Hooks apply to data objects only, never to the storeObject/meta row.
 *
 * @property {Function} [hooks.transform]
 *   Transforms each data object during migration.
 *
 *   @param {Object} obj - The data object being transformed
 *   @param {Object} ctx - Context object
 *   @param {string} ctx.stepVersion - The version of this migration step
 *   @param {Object} ctx.model - The target dataObject model
 *   @param {Object} ctx.utils - Utility functions
 *   @param {Function} ctx.utils.keyPruning - Removes keys not in model
 *   @returns {Object} The transformed object
 *
 *   Common Use Cases:
 *   - Remove deprecated fields with keyPruning
 *   - Transform field values
 *   - Migrate data from old structure to new
 *
 *   @example
 *   transform: (obj, ctx) => {
 *     console.debug(`[settings@${ctx.stepVersion}] transform`, Object.keys(obj));
 *
 *     // Remove fields no longer in the model
 *     ctx.utils.keyPruning(obj, ctx.model);
 *
 *     // Transform a field
 *     if (obj.oldField) {
 *       obj.newField = obj.oldField.toUpperCase();
 *       delete obj.oldField;
 *     }
 *
 *     return obj;
 *   }
 *
 * @property {Function} [hooks.beforePopulate]
 *   Runs before data population begins.
 *
 *   @param {Object} params
 *   @param {IDBDatabase} params.db - IndexedDB database instance
 *   @param {string} params.store - Store name
 *   @param {string} params.version - Migration version
 *   @returns {Promise<void>}
 *
 *   Use Cases:
 *   - Prepare data structures
 *   - Validate preconditions
 *   - Set up temporary state
 *
 * @property {Function} [hooks.afterPopulate]
 *   Runs after data population completes.
 *
 *   @param {Object} params
 *   @param {IDBDatabase} params.db - IndexedDB database instance
 *   @param {string} params.store - Store name
 *   @param {string} params.version - Migration version
 *   @returns {Promise<void>}
 *
 *   Use Cases:
 *   - Clean up temporary data
 *   - Trigger post-migration actions
 *   - Validate migration success
 */
// #endregion
export default {
  /**
   * version  MAJOR.MINOR.PATCH
   *
   * For new store, set version at 1.1.1
   *
   * To apply patch data object key values, increment position 3 (1.1.2, so 1.1.1 -> 1.1.2)
   * To apply minor "model" updates, increment position 2 (1.2.1, so 1.1.1 || 1.1.2 || 1.1.6 -> 1.2.1)
   * To apply MAJOR "store" updates, increment postion 1 (2.1.1, so 1.2.1 || 1.2.7 || 1.9.4 -> 2.1.1)
   *
   * Versions cannot jump sequence
   *   (e.g. ver 1.1.1 cannot jump to 1.1.5, ver 1.1.2 cannot jump to 1.2.2, it must go to 1.2.1)
   *
   * @property {string} version - REQUIRED Version used in update and must match version in file name
   */
  version: VERSION,

  /**
   * forceUpdate
   *
   * to be used when updates are required not optional by user
   *
   * true  this will skip the user prompt
   * false this will allow user to skip update
   *
   * ** FUTURE behavior will be to prompt user to backup first and then force the update
   *
   * @property {boolean} forceUpdate - force update without allowing user to skip
   */
  forceUpdate: false,

  /**
   * type
   *
   * Type options (MAJOR || MINOR || PATCH)
   *
   * This value is for convenience and quick reference and is not used to determine any update decisions in code
   *
   * @property {string} type - Human-readable indication representing the version number update type MAJOR.MINOR.PATCH
   * */
  type: "MAJOR",

  /**
   * changeLog
   *
   * Intended for documentation purposes but could be used for other purposes as well
   *
   * @property {string[]} changeLog - Array of specific changes this config contains
   */
  changeLog: [
    "- create store and indexes",
    "- establish store and data models",
    "- populate store with initial tracks",
  ],

  /**
   * changeDescription
   *
   * Description of changes in this config update. Intended to be informative to app user
   *  if prompted to update
   *
   * @property {string} changeDescription - Public description of update
   */
  changeDescription: "Initiate Tracks store as part of TOAT feature.",

  /**
   * stores (MAJOR)
   *
   * ONLY supports a single store defined per config, new store = new config file
   *  additional configured stores will be ignored
   *
   * Store modifications require a database version bump
   *
   *  + to modify indexes (adding), add index configuration, create a MAJOR update config file
   *  - to modify indexes (removing), remove index, leaving other indexes in place, crate MAJOR update config file
   *
   * @constant {Object} stores Accepts only one store and schema definition
   */
  stores: {
    tracks: "id,taleId,title,type",
  },

  /**
   * Models (MINOR)
   *
   * Minor updates will make configured change to ALL data objects
   * For storeObject
   *  + to modify model (adding keys), add key and create a MINOR update config file
   *  - to modify model (removing keys), remove key leaving other keys
   *
   * For dataObject
   *  + to modify model (adding keys), add key and create a MINOR update config file
   *  - to modify model (removing keys), remove key leaving other keys and configure
   *    transform hook to run keyPruning. Keys are not removed automatically
   *
   * @constant {Object} storeObject Defines model for single store object
   * @constant {Object} dataObject Defines model for all data objects
   */
  model: {
    storeObject: modelStore,
    dataObject: modelData,
  },

  dataPolicies: {
    id: {
      generate: "all", // keep || all ||  invalid (default=keep)
      //   action: ({ store, index }) => `${store}-${index + 1}`,
    },
  },
  /**
   * data (PATCH)
   *
   * When store is initiated (v1.1.1)
   * - Seed the storeObject row in data for this store if you want to lock their starting versions.
   * - Fallback will create an object using model and will only apply version and date/time stamps
   *
   * @property {Array.<{...dataObject}>} data define data items to add to store for this patch update
   *    if no data needs to be added, keep array empty
   */
  data: [
    // The store's own meta row (id must equal the store name)
    {
      id: "tracks",
      label: "Tracks",
      type: "feature",
      description: "Tracks are a way to tell your tale as you travel.",
      categories: [],
      image: "",
      version: "1.1.1",
      created: 1764022747586,
      modified: 1764022747586,
    },
    {
      coordinates: {
        accuracy: 35,
        altitude: 868.2667751312256,
        heading: null,
        latitude: 43.567661029339696,
        longitude: -116.22052314202834,
        speed: null,
      },
      title: "On time at the gate and ready to fly. ",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Boise",
      id: "fuy7sx7ddu1h15mzkfhxtz01",
      type: "tracks",
      subType: "tracks",
      created: 1707222527719,
      updated: 1747935114169,
    },
    {
      coordinates: {
        accuracy: 35,
        altitude: 1641.8862590789795,
        heading: null,
        latitude: 39.853884976638305,
        longitude: -104.68068481273967,
        speed: null,
      },
      title: "Feet on the ground in Denver. 25 mins early!",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Denver",
      id: "gnvr6s0t6m7ssnfx6djb5ng1",
      type: "tracks",
      subType: "tracks",
      created: 1707236241662,
      updated: 1747935114169,
    },
    {
      coordinates: {
        accuracy: 1414,
        altitude: null,
        heading: null,
        latitude: 10.58777507957731,
        longitude: -85.55279990594013,
        speed: null,
      },
      title: "Hola Costa Rica!  Pura Vida",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "a47pmjjlxgozj5nm7py6qw2r",
      type: "tracks",
      subType: "tracks",
      created: 1707255730750,
      updated: 1747935114169,
    },
    {
      coordinates: {
        accuracy: 16.368905421489124,
        altitude: 710.8503266144544,
        heading: null,
        latitude: 10.713256928206901,
        longitude: -85.04099978320878,
        speed: 0.28424665331840515,
      },
      title:
        "Made it to Heliconias Rainforest lodge in the dark, one wrong turn. ",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "rn6w372auqwoksqn34o4sf23",
      type: "tracks",
      subType: "tracks",
      created: 1707273566830,
      updated: 1747935114169,
    },
    {
      coordinates: {
        accuracy: 23664,
        altitude: 727.4404296875,
        heading: null,
        latitude: 10.716168116628873,
        longitude: -85.03856753903739,
        speed: null,
      },
      title: "Heliconias…first hanging bridge",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "bqq7vuyxhxq7hqkkupyqlm8s",
      type: "tracks",
      subType: "tracks",
      created: 1707327952021,
      updated: 1747935114170,
    },
    {
      coordinates: {
        accuracy: 8.592144035920906,
        altitude: 710.5392753854394,
        heading: null,
        latitude: 10.713062032196746,
        longitude: -85.04160201693912,
        speed: 0,
      },
      title: "Carrie spots our first 3 toed sloth. Thought it was a nest. ",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "d3w5wtgn5o36j2rrrp91m39e",
      type: "tracks",
      subType: "tracks",
      created: 1707354349064,
      updated: 1747935114170,
    },
    {
      coordinates: {
        accuracy: 14.980998999313407,
        altitude: 418.43863287845943,
        heading: 140.75167914942756,
        latitude: 10.702319,
        longitude: -84.994517,
        speed: 0.359920859336853,
      },
      title:
        "Conquered Rio Celeste. Very wet, lots of stairs, beautiful blue water. ",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "gdmh5oewpmrum2w8qshaf2po",
      type: "tracks",
      subType: "tracks",
      created: 1707420600000,
      updated: 1747935114170,
    },
    {
      coordinates: {
        accuracy: 4.745245348310794,
        altitude: 419.24745712894946,
        heading: null,
        latitude: 10.454456114203067,
        longitude: -84.66676403765126,
        speed: 0.5254927365802985,
      },
      title: "Arrived Brisas Arenal, cold showers. No heat.",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "xnfm5cc22i72c5y6vtp5smdf",
      type: "tracks",
      subType: "tracks",
      created: 1707432862167,
      updated: 1747935114171,
    },
    {
      coordinates: {
        accuracy: 8.006056602822296,
        altitude: 253.70098670665175,
        heading: null,
        latitude: 10.472114751492057,
        longitude: -84.64321571439937,
        speed: 0,
      },
      title: "Hangry at Restaurante Nene’s, great Tres Leches desert.",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "h9q2oql3mdgxx4ocex7a5tgq",
      type: "tracks",
      subType: "tracks",
      created: 1707435150691,
      updated: 1747935114171,
    },
    {
      coordinates: {
        accuracy: 12.087697975348439,
        altitude: 613.2024434022605,
        heading: null,
        latitude: 10.488177867363325,
        longitude: -84.75367222820347,
        speed: 0.2860339283943176,
      },
      title: "Mystico Arenal Hanging bridges. A no drop zone. ",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "lvcfpblpan4fywoamoa6nf6s",
      type: "tracks",
      subType: "tracks",
      created: 1707491341634,
      updated: 1747935114171,
    },
    {
      coordinates: {
        accuracy: 7.585499461383689,
        altitude: 684.8351789834377,
        heading: 293.19349265165624,
        latitude: 10.476389557392894,
        longitude: -84.72434934708664,
        speed: 0.008157284930348396,
      },
      title: "Arenal volcano Hike. Second sloth sighting.",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "z5686nfzqq6io0pov236lpeg",
      type: "tracks",
      subType: "tracks",
      created: 1707516065523,
      updated: 1747935114171,
    },
    {
      coordinates: {
        accuracy: 14.963311271585903,
        altitude: 628.3483827189465,
        heading: 98.0542890265366,
        latitude: 10.518696942078126,
        longitude: -84.86137267813365,
        speed: 0.19756481051445007,
      },
      title: "Visiting a 500 year old Ceiba Tree",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "ta7ckxh4st1ug3ya984tprdk",
      type: "tracks",
      subType: "tracks",
      created: 1707586719680,
      updated: 1747935114172,
    },
    {
      coordinates: {
        accuracy: 35.00022411552663,
        altitude: 622.569326043129,
        heading: null,
        latitude: 10.545238803788843,
        longitude: -84.8922480278384,
        speed: null,
      },
      title:
        "Met Elkin at Farmacia Organica. Soooo nice and wonderful place stayed a while to eat ice cream and chat. ",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "zym5cz2husz8qe6ebnwhm6yz",
      type: "tracks",
      subType: "tracks",
      created: 1707590602702,
      updated: 1747935114172,
    },
    {
      coordinates: {
        accuracy: 42.001849582997956,
        altitude: 90.56105720996857,
        heading: null,
        latitude: 10.265487786021733,
        longitude: -85.02072827386998,
        speed: null,
      },
      title:
        "Food stop at Restaurante Mi Finca on our way. Will get to see the scarlet Mccaw here too. ",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "ufwq46w1kzuqpckeh0lcg8u9",
      type: "tracks",
      subType: "tracks",
      created: 1707597218144,
      updated: 1747935114173,
    },
    {
      coordinates: {
        accuracy: 35,
        altitude: 13.860869884490967,
        heading: null,
        latitude: 9.950018654086435,
        longitude: -85.66977835966247,
        speed: null,
      },
      title:
        "Arrived in Nosara. Bumpy roads, the last of a sunset and leftovers. ",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "qcbcqp7hzn9fd8adhf6tuere",
      type: "tracks",
      subType: "tracks",
      created: 1707612426792,
      updated: 1747935114173,
    },
    {
      coordinates: {
        accuracy: 16.045477902788864,
        altitude: 14.269864956472432,
        heading: 0.6439909541477385,
        latitude: 9.950081092866874,
        longitude: -85.66998247940585,
        speed: 0.04951736703515053,
      },
      title:
        "Just soaked up 88 degrees at Guiones Beach and chased it with a fresh lunch at Naked foods. ",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "kw9wgnjsqis6xciq4ei88qvn",
      type: "tracks",
      subType: "tracks",
      created: 1707684909373,
      updated: 1747935114173,
    },
    {
      coordinates: {
        accuracy: 12.373754830099422,
        altitude: 8.05268994924364,
        heading: 231.43470748985445,
        latitude: 9.880510169321875,
        longitude: -85.52839028916968,
        speed: 0,
      },
      title:
        "Samara, our favorite beach. La Dolce Vita our favorite lunch spot. ",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "zkdlm0js1w1zmwjt99idpqi3",
      type: "tracks",
      subType: "tracks",
      created: 1707763502312,
      updated: 1747935114174,
    },
    {
      coordinates: {
        accuracy: 10,
        altitude: 17.8,
        heading: 277.5,
        latitude: 9.871833500000001,
        longitude: -85.49350066666666,
        speed: 0.05144444444444445,
      },
      title: "Playa Carillio. Beautiful empty beach. ",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "ixy3ghp8t0cldi4fyw61worv",
      type: "tracks",
      subType: "tracks",
      created: 1707771158065,
      updated: 1747935114174,
    },
    {
      coordinates: {
        accuracy: 4.769027451463884,
        altitude: 73.4612289359793,
        heading: null,
        latitude: 9.857685528083264,
        longitude: -85.40177506048697,
        speed: 0.09721201136893905,
      },
      title:
        "Arrived in Punta Islita around 3:15. Amazing resort. Monkeys, McCaws, sunsets. And laundry. ",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "hwd51234odsprr1rgoal2oo0",
      type: "tracks",
      subType: "tracks",
      created: 1707782801439,
      updated: 1747935114174,
    },
    {
      coordinates: {
        accuracy: 38.666666666666664,
        altitude: 72.59742879867554,
        heading: null,
        latitude: 9.861899171364724,
        longitude: -85.40386257535667,
        speed: null,
      },
      title: "Visited the Wild Macaw Reserve. ",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "ncnksinhyp5lgl013a8874y8",
      type: "tracks",
      subType: "tracks",
      created: 1707951373190,
      updated: 1747935114175,
    },
    {
      coordinates: {
        accuracy: 9.744319470709423,
        altitude: 7.965935654433862,
        heading: 15.692406410724663,
        latitude: 10.578009326971081,
        longitude: -85.67686558056505,
        speed: 0.03829886391758919,
      },
      title: "Lunch at Roberto’s in Playa Hermosa at the beach. ",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "od30o9a37hy6jmbnki42nnc2",
      type: "tracks",
      subType: "tracks",
      created: 1708023162192,
      updated: 1747935114175,
    },
    {
      coordinates: {
        accuracy: 16.881432483523067,
        altitude: 7.568254293873906,
        heading: null,
        latitude: 10.548846861721263,
        longitude: -85.69753465912592,
        speed: 0,
      },
      title: "Our final stop this trip, Del Coco. HOT! 🥵 ",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "le26mvbih5pra3go8bzl7p1q",
      type: "tracks",
      subType: "tracks",
      created: 1708029687772,
      updated: 1747935114175,
    },
    {
      coordinates: {
        accuracy: 35,
        altitude: 88.18395256996155,
        heading: null,
        latitude: 10.600569400614095,
        longitude: -85.53523701867667,
        speed: null,
      },
      title: "It’s time to leave. 😩 See you again soon CR. Pura Vida",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Costa_Rica",
      id: "ree1jn2pg80iq6z7x3ezqm1f",
      type: "tracks",
      subType: "tracks",
      created: 1708105627313,
      updated: 1747935114175,
    },
    {
      coordinates: {
        accuracy: 35,
        altitude: 1640.7368354797363,
        heading: null,
        latitude: 39.858645649658115,
        longitude: -104.67430221766826,
        speed: null,
      },
      title: "Back in the US. Need food. ",

      taleId: "rwkntocyn9q73xuwux5aqd0g",
      timezone: "America/Denver",
      id: "vml393mvp4f6m6ovz4se02pk",
      type: "tracks",
      subType: "tracks",
      created: 1708132142291,
      updated: 1747935114176,
    },
    {
      coordinates: {
        accuracy: 11.588584632432589,
        altitude: 7.256461873542776,
        heading: null,
        latitude: 47.455602,
        longitude: -122.259941,
        speed: null,
      },
      title: "Simply Thai with Matthew",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "e5h6hi5yr19o79wpq4byw8rw",
      type: "tracks",
      subType: "tracks",
      created: 1749350818713,
      updated: 1749350818713,
    },
    {
      coordinates: {
        accuracy: 4.489399443453107,
        altitude: 106.72160599660128,
        heading: 20.46814098009144,
        latitude: 47.642002,
        longitude: -122.136281,
        speed: 0,
      },
      title: "Microsoft!",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "oihdk2atrj8xpvkglou7dely",
      type: "tracks",
      subType: "tracks",
      created: 1749403205237,
      updated: 1749403205237,
    },
    {
      coordinates: {
        accuracy: 6.1643429473650935,
        altitude: 106.2641654163599,
        heading: 117.16050688156685,
        latitude: 47.654579,
        longitude: -122.142782,
        speed: 0,
      },
      title: "Seattle…your roads suck!",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "v74zpv3ngj2fs68rjp7opkfh",
      type: "tracks",
      subType: "tracks",
      created: 1749405173227,
      updated: 1749405173227,
    },
    {
      coordinates: {
        accuracy: 11.694862855790596,
        altitude: 8.61558610573411,
        heading: 322.6821361559676,
        latitude: 48.51703,
        longitude: -122.612457,
        speed: 0.36987626552581726,
      },
      title: "Cosmic Veggie Lunch. Yum!",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "hs0owytbbol03alei4n01hhz",
      type: "tracks",
      subType: "tracks",
      created: 1749414350578,
      updated: 1749414350578,
    },
    {
      coordinates: {
        accuracy: 3.372308514222997,
        altitude: 10.458446298725903,
        heading: null,
        latitude: 48.505,
        longitude: -122.678814,
        speed: 0,
      },
      title: "Waiting on the Ferry. Lane 12",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "f7s0112qgwwn6jhmp3rudoob",
      type: "tracks",
      subType: "tracks",
      created: 1749417985270,
      updated: 1749417985270,
    },
    {
      coordinates: {
        accuracy: 14.764509938208478,
        altitude: -7.778235928065367,
        heading: 293.58358619954106,
        latitude: 48.531569,
        longitude: -122.816218,
        speed: 8.621569633483887,
      },
      title: "Thatcher Pass. Riden’ the Ferry",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "xnr8rsdeh8f054ttxnpjo1pb",
      type: "tracks",
      subType: "tracks",
      created: 1749423371557,
      updated: 1749423371557,
    },
    {
      coordinates: {
        accuracy: 12.472611666347635,
        altitude: 150.21293822584596,
        heading: null,
        latitude: 48.696469,
        longitude: -122.84037,
        speed: 0.4417097270488739,
      },
      title: "Arrived! Beautiful view from our patio",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "ld9r7pe9e3d0a8l34rwayyut",
      type: "tracks",
      subType: "tracks",
      created: 1749430167244,
      updated: 1749430167244,
    },
    {
      coordinates: {
        accuracy: 5.410564586202071,
        altitude: 9.848707824014127,
        heading: 86.97653685768543,
        latitude: 48.695268,
        longitude: -122.906607,
        speed: 0.9623786211013685,
      },
      title: "Madrona Bar and Grill last night 🥵",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "p066ujv4c85p097iqd2aebuw",
      type: "tracks",
      subType: "tracks",
      created: 1749497818965,
      updated: 1749497818965,
    },
    {
      coordinates: {
        accuracy: 7.798524739452171,
        altitude: 12.281030756421387,
        heading: null,
        latitude: 48.695537,
        longitude: -122.90623,
        speed: 0.07922758907079697,
      },
      title: "Orcas Food Coop 👍",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "h1x7ht51euh5arg1gi4gh7cs",
      type: "tracks",
      subType: "tracks",
      created: 1749498487290,
      updated: 1749498487290,
    },
    {
      coordinates: {
        accuracy: 11.78406394826964,
        altitude: 12.747959445850345,
        heading: null,
        latitude: 48.694929,
        longitude: -122.905422,
        speed: 0.09812659025192261,
      },
      title: "Lower Tavern. Finally GF and Vegan options 🙌",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "jytnhey4zcow81b70qy5ugyq",
      type: "tracks",
      subType: "tracks",
      created: 1749499706205,
      updated: 1749499706205,
    },
    {
      coordinates: {
        accuracy: 16.507652794429664,
        altitude: 1.2983535003021807,
        heading: 302.88448305475555,
        latitude: 48.681118,
        longitude: -123.166451,
        speed: 5.491056442260742,
      },
      title: "No Whale sightings yet. ",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "hgwvi3gmbk68uqwcwdc4bumi",
      type: "tracks",
      subType: "tracks",
      created: 1749582071905,
      updated: 1749582071905,
    },
    {
      coordinates: {
        accuracy: 8.39078494169306,
        altitude: 1.9586444767192006,
        heading: 231.71183337993747,
        latitude: 48.691173,
        longitude: -123.239663,
        speed: 2.9799493893772784,
      },
      title: "Top of the US",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "gmdvaj3ugo1aajxuqevtg10y",
      type: "tracks",
      subType: "tracks",
      created: 1749582857899,
      updated: 1749582857899,
    },
    {
      coordinates: {
        accuracy: 5.990173907355514,
        altitude: 1.3984874607995152,
        heading: 177.0701389702242,
        latitude: 48.594381,
        longitude: -123.206464,
        speed: 8.583349227897502,
      },
      title: "Skirtin’ Canada 🇨🇦",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "f22lx14uhyjtvapwjdi9pavy",
      type: "tracks",
      subType: "tracks",
      created: 1749584626759,
      updated: 1749584626759,
    },
    {
      coordinates: {
        accuracy: 7.532122165869778,
        altitude: 12.51755179837346,
        heading: null,
        latitude: 48.641532,
        longitude: -122.9796543,
        speed: 0.11051198840141296,
      },
      title: "South Turtleback Trailhead",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "jnus3ejh3lj8i2kcv70ld89z",
      type: "tracks",
      subType: "tracks",
      created: 1749680944594,
      updated: 1749680944594,
    },
    {
      coordinates: {
        accuracy: 9.347490841202434,
        altitude: 730.1107656257227,
        heading: null,
        latitude: 48.677516,
        longitude: -122.831185,
        speed: 0.03565008565783501,
      },
      title: "Mt Constitution Lookout Tower",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "svezmi3glic5l2p1tpxqc6dq",
      type: "tracks",
      subType: "tracks",
      created: 1749765622396,
      updated: 1749765622396,
    },
    {
      coordinates: {
        accuracy: 40.236111970160024,
        altitude: 154.63552675825795,
        heading: null,
        latitude: 48.6462558,
        longitude: -122.842704,
        speed: null,
      },
      title: "Cascade Falls hike. Moran State Park ",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "svaveyri3dkj5uw2g3885z7d",
      type: "tracks",
      subType: "tracks",
      created: 1749773592025,
      updated: 1749773592025,
    },
    {
      coordinates: {
        accuracy: 11.091942592500144,
        altitude: 5.631041175968324,
        heading: null,
        latitude: 48.694615,
        longitude: -122.908278,
        speed: 0.13857227563858032,
      },
      title: "Scallops at New Leaf",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "ve2ici9uwao66usdrxcqd6ci",
      type: "tracks",
      subType: "tracks",
      created: 1749775762881,
      updated: 1749775762881,
    },
    {
      coordinates: {
        accuracy: 9.55168738791206,
        altitude: 13.707058289363601,
        heading: null,
        latitude: 48.696369,
        longitude: -122.905711,
        speed: null,
      },
      title: "Drinks at The Barnacle",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "c3p7g7lbdlzhtn96kanbyzn6",
      type: "tracks",
      subType: "tracks",
      created: 1749782009550,
      updated: 1749782009550,
    },
    {
      coordinates: {
        accuracy: 27.193517961295946,
        altitude: 19.169855273736232,
        heading: null,
        latitude: 48.694648,
        longitude: -122.90427,
        speed: 0.8266209363937378,
      },
      title: "Late lunch at Lone Pine Larder",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "w40dfsn64tdlaxzo8tk8dtr4",
      type: "tracks",
      subType: "tracks",
      created: 1749848607526,
      updated: 1749848607526,
    },
    {
      coordinates: {
        accuracy: 27.117378716225964,
        altitude: 35.683475086576465,
        heading: null,
        latitude: 48.683194,
        longitude: -122.961563,
        speed: null,
      },
      title: "Orcas Island Pottery $$$$$",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "pggmc3nyd1vlwtuhwh714j7n",
      type: "tracks",
      subType: "tracks",
      created: 1749855021265,
      updated: 1749855021265,
    },
    {
      coordinates: {
        accuracy: 15.703597988260789,
        altitude: 160.06741095471796,
        heading: null,
        latitude: 48.696393,
        longitude: -122.840336,
        speed: 0.2653895616531372,
      },
      title: "Drinks on the patio with a Hot Toddy",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "ghau0518rsoxz6oksdvuvd65",
      type: "tracks",
      subType: "tracks",
      created: 1749860596882,
      updated: 1749860596882,
    },
    {
      coordinates: {
        accuracy: 14,
        altitude: 11.428689411176034,
        heading: 268.80242919921875,
        latitude: 48.696447,
        longitude: -122.906755,
        speed: 0.4292081892490387,
      },
      title: "Farmers Market at the Village Green ",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "m0hr0o2lu98upvjxf010kym3",
      type: "tracks",
      subType: "tracks",
      created: 1749923463280,
      updated: 1749923463280,
    },
    {
      coordinates: {
        accuracy: 9.475552064996966,
        altitude: 23.280953096225858,
        heading: 99.65361488444815,
        latitude: 48.598342,
        longitude: -122.945416,
        speed: 0,
      },
      title: "Late lunch at Harmony Hive Bistro 👍",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "s9zyy3yfxdt3s0zvgj6pydig",
      type: "tracks",
      subType: "tracks",
      created: 1749934204888,
      updated: 1749934204888,
    },
    {
      coordinates: {
        accuracy: 6.43426665818257,
        altitude: 6.921685879118741,
        heading: null,
        latitude: 48.51659,
        longitude: -122.611537,
        speed: 0,
      },
      title: "Big Mama Thornton @ Sugar Mamas ",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "x0dn2yokhlotw20r1119g3p1",
      type: "tracks",
      subType: "tracks",
      created: 1749953572115,
      updated: 1749953572115,
    },
    {
      coordinates: {
        accuracy: 14.46365436159149,
        altitude: 6.8559221495112475,
        heading: null,
        latitude: 48.518164,
        longitude: -122.611376,
        speed: 0.2419666200876236,
      },
      title: "Fish Tacos at Adrift",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "ototwqo343uh7xc4csg5wlic",
      type: "tracks",
      subType: "tracks",
      created: 1749959457970,
      updated: 1749959457970,
    },
    {
      coordinates: {
        accuracy: 17.42834447752298,
        altitude: 8.883724804972545,
        heading: null,
        latitude: 48.516988,
        longitude: -122.612537,
        speed: null,
      },
      title: "Alley Cat - Vintage Shopping 👍",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "jbkq9mp327f0hdga4cw6p48i",
      type: "tracks",
      subType: "tracks",
      created: 1750011067440,
      updated: 1750011067440,
    },
    {
      coordinates: {
        accuracy: 11.43368459468644,
        altitude: 352.6704557752237,
        heading: 188.9920203433899,
        latitude: 47.595558,
        longitude: -120.659329,
        speed: 0.11262878570025657,
      },
      title: "Hello Leavenworth 🇩🇪",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "f6pfxzzven2rkfcpubi38wkw",
      type: "tracks",
      subType: "tracks",
      created: 1750034456656,
      updated: 1750034456656,
    },
    {
      coordinates: {
        accuracy: 12.088988514916258,
        altitude: 350.13258718189064,
        heading: null,
        latitude: 47.59428,
        longitude: -120.663618,
        speed: 0.08598565310239792,
      },
      title: "Yodelins Great food, view and music",

      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      timezone: "America/Los_Angeles",
      id: "oxxgq19il84s46et8okfw7ga",
      type: "tracks",
      subType: "tracks",
      created: 1750037785101,
      updated: 1750037785101,
    },
    {
      coordinates: {
        accuracy: 13.985544677748631,
        altitude: 312.75598807132076,
        heading: null,
        latitude: 33.639645,
        longitude: -84.439194,
        speed: 0.19954293966293335,
      },
      title: "Food!! Atlanta Bread and a Hot Honey Old Fashioned ",

      taleId: "a3y4oi0kssjimi59mba751r1",
      timezone: "America/New_York",
      id: "xwgntevwm6hjbepthyg8alhq",
      type: "tracks",
      subType: "tracks",
      created: 1761438075383,
      updated: 1761438075383,
    },
    {
      coordinates: {
        accuracy: 5.624997055259406,
        altitude: 31.675237145805863,
        heading: 146.85727184858695,
        latitude: 30.495799,
        longitude: -86.547721,
        speed: 0.1021633893251419,
      },
      title: "Touchdown!!  Everything arrived. Next stop…sandy beach. ",

      taleId: "a3y4oi0kssjimi59mba751r1",
      timezone: "America/Chicago",
      id: "kdy6op9p9hv1yjht27rftxx4",
      type: "tracks",
      subType: "tracks",
      created: 1761448430338,
      updated: 1761448430338,
    },
    {
      coordinates: {
        accuracy: 7.018854318213282,
        altitude: 8.443277035897264,
        heading: null,
        latitude: 30.347128,
        longitude: -86.229652,
        speed: 0,
      },
      title: "Shunk Gulley - grouper for lunch",

      taleId: "a3y4oi0kssjimi59mba751r1",
      timezone: "America/Chicago",
      id: "utnwheapudukxchufx9h36rb",
      type: "tracks",
      subType: "tracks",
      created: 1761498548381,
      updated: 1761498548381,
    },
    {
      coordinates: {
        accuracy: 21.96162599854947,
        altitude: 18.21684214028591,
        heading: null,
        latitude: 30.347205,
        longitude: -86.229778,
        speed: 0.23235370218753815,
      },
      title: "⭐️⭐️⭐️⭐️⭐️ Shunk Gulley",

      taleId: "a3y4oi0kssjimi59mba751r1",
      timezone: "America/Chicago",
      id: "d3hc18u0fteww73y02rpymoj",
      type: "tracks",
      subType: "tracks",
      created: 1761501149579,
      updated: 1761501149579,
    },
    {
      coordinates: {
        accuracy: 33.46160768287715,
        altitude: 17.5100626112107,
        heading: null,
        latitude: 30.339914,
        longitude: -86.206521,
        speed: null,
      },
      title: "Watched a pod of Porpoises swim by",

      taleId: "a3y4oi0kssjimi59mba751r1",
      timezone: "America/Chicago",
      id: "v6duphd4yrhekcs3roxmhcpc",
      type: "tracks",
      subType: "tracks",
      created: 1761603100751,
      updated: 1761603100751,
    },
    {
      coordinates: {
        accuracy: 17.954477400967125,
        altitude: 7.442287966531143,
        heading: null,
        latitude: 30.32206,
        longitude: -86.145389,
        speed: null,
      },
      title: "Fish out of Water.  Yum! ⭐️⭐️⭐️⭐️⭐️",

      taleId: "a3y4oi0kssjimi59mba751r1",
      timezone: "America/Chicago",
      id: "j875496r4wn6iqqdfpog40ho",
      type: "tracks",
      subType: "tracks",
      created: 1761608978935,
      updated: 1761608978935,
    },
    {
      coordinates: {
        accuracy: 13.027505046488091,
        altitude: 10.954072840396442,
        heading: null,
        latitude: 30.284414,
        longitude: -86.027354,
        speed: 0.2457384169101715,
      },
      title: "Lunch at Georges with Kendall",

      taleId: "a3y4oi0kssjimi59mba751r1",
      timezone: "America/Chicago",
      id: "anl8xiijuf9lk60nun5h9r4l",
      type: "tracks",
      subType: "tracks",
      created: 1761673924255,
      updated: 1761673924255,
    },
    {
      coordinates: {
        accuracy: 26.035831023960846,
        altitude: 17.84904899597168,
        heading: null,
        latitude: 30.345895,
        longitude: -86.206623,
        speed: null,
      },
      title: "Thai date night",

      taleId: "a3y4oi0kssjimi59mba751r1",
      timezone: "America/Chicago",
      id: "rlemdqbaytbwm27mow3kzpsz",
      type: "tracks",
      subType: "tracks",
      created: 1761779949526,
      updated: 1761779949526,
    },
    {
      coordinates: {
        accuracy: 13.29923450721021,
        altitude: 2.105446879377805,
        heading: 99.10164088646793,
        latitude: 30.391384,
        longitude: -86.499175,
        speed: 2.204387903213501,
      },
      title: "Sunset cruise 🌅",

      taleId: "a3y4oi0kssjimi59mba751r1",
      timezone: "America/Chicago",
      id: "vlrjkbf1to7t217slqonlxi1",
      type: "tracks",
      subType: "tracks",
      created: 1761948293323,
      updated: 1761948293323,
    },
    {
      coordinates: {
        accuracy: 13.45934236588438,
        altitude: 2.089232704871801,
        heading: 12.423408562218999,
        latitude: 30.391908,
        longitude: -86.503955,
        speed: 1.3066906929016113,
      },
      title: "Saw lots of Dolphins on our cruise 🐬",

      taleId: "a3y4oi0kssjimi59mba751r1",
      timezone: "America/Chicago",
      id: "i9bsja0dkvhbce71u0bht1pq",
      type: "tracks",
      subType: "tracks",
      created: 1761953241161,
      updated: 1761953241161,
    },
    {
      coordinates: {
        accuracy: 17.39796046395274,
        altitude: 5.090839385986328,
        heading: null,
        latitude: 30.391834,
        longitude: -86.49701,
        speed: null,
      },
      title: "Harbor Docks for some Triple Tail",

      taleId: "a3y4oi0kssjimi59mba751r1",
      timezone: "America/Chicago",
      id: "oagnavui1qyuf3d0v5nlgw2z",
      type: "tracks",
      subType: "tracks",
      created: 1761955157351,
      updated: 1761955157351,
    },
    {
      coordinates: {
        accuracy: 8.66851436226287,
        altitude: 259.1000061035156,
        heading: null,
        latitude: 44.884143,
        longitude: -93.211176,
        speed: null,
      },
      title: "MN Airport. The best and great food",

      taleId: "a3y4oi0kssjimi59mba751r1",
      timezone: "America/Chicago",
      id: "qdrrmu8s1b4el61hpcytlm2z",
      type: "tracks",
      subType: "tracks",
      created: 1762030126769,
      updated: 1762030126769,
    },
  ],

  /**
   * Hooks
   *
   * - hooks are for data objects and do not apply to storeObjects
   */
  hooks: {
    // Will run for *data rows only* (never for the storeObject/meta row)
    // transform: (obj, ctx) => {
    //   console.debug(
    //     `[settings@${ctx.stepVersion}] transform`,
    //     Object.keys(obj)
    //   );
    //   // Only prune in *this* version, e.g. when you removed fields
    //   ctx.utils.keyPruning(obj, ctx.model);
    //   return obj;
    // },
    // beforePopulate: async ({ db, store, version, cfg}) => {},
    // afterPopulate: async ({ db, store, version, cfg }) => {},
    //   beforePopulate: async ({ db, store, version, cfg }) => {
    //     const generate = cfg?.dataPolicies?.id?.generate || null;
    //     const shouldGenerateAll = generate === "all";
    //     const shouldGenerateInvalid = generate === "invalid";
    //     const data = cfg.data;
    //     const dataLength = data.length;
    //     for (let i = 0; i < dataLength; i++) {
    //       const rec = data[i];
    //       if (rec.id === cfg.store) continue;
    //       if (shouldGenerateAll) {
    //         rec.id = createId();
    //       } else if (shouldGenerateInvalid && !isValidCuid2(rec.id)) {
    //         rec.id = createId();
    //       }
    //     }
    //     console.debug(data);
    //   },
  },
};

/* eslint-enable import/no-anonymous-default-export */
